import { useQuery } from '@tanstack/react-query';

import { bulkGetVocabularyTerms, termCategoryOf } from '@/api/vocabularies';

import {
  HPO_CATEGORIES,
  HPO_CATEGORY_OTHER,
} from '@/features/patient-record/data/hpoCategories';

/**
 * Resolve `hpoId → category-root-id` for the supplied phenotype list.
 * Used by the Phenotypes section's "Group by body system" toggle.
 *
 * The vocabulary service exposes each term's `term_category` (the
 * curated ancestor-set used elsewhere by the picker subtree filter);
 * a single `bulkGetVocabularyTerms` call hydrates every visible row.
 * Cached for five minutes per id-set so toggling the group view
 * doesn't refetch on each render.
 *
 * Returns `HPO_CATEGORY_OTHER` for HPO terms that don't match any
 * curated body-system root (custom / off-catalogue terms, or terms
 * filed under roots we don't surface yet).
 */
export function usePhenotypeCategories(
  hpoIds: readonly string[],
): {
  readonly data: ReadonlyMap<string, string>;
  readonly isLoading: boolean;
} {
  const sorted = [...hpoIds].filter((id) => id.length > 0).sort();
  const query = useQuery({
    queryKey: ['vocabulary', 'hpo', 'categorize', sorted],
    queryFn: ({ signal }) => bulkGetVocabularyTerms('hpo', sorted, signal),
    enabled: sorted.length > 0,
    staleTime: 5 * 60_000,
  });

  const map = new Map<string, string>();
  for (const term of query.data ?? []) {
    map.set(term.id, classifyTerm(term.id, termCategoryOf(term)));
  }
  return { data: map, isLoading: query.isPending && sorted.length > 0 };
}

function classifyTerm(id: string, termCategory: readonly string[]): string {
  for (const c of HPO_CATEGORIES) {
    if (id === c.id) return c.id;
    if (termCategory.includes(c.id)) return c.id;
  }
  return HPO_CATEGORY_OTHER;
}
